import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    VariableTab,
    createCategory,
} from '../../services/api/departmentVariablesService';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Input } from '../ui/input';

interface AddCategoryFormProps {
    tab: VariableTab;
    takenTitles: string[];
    onSaved: () => Promise<void>;
    onClose: () => void;
}

export default function AddCategoryForm({
    tab,
    takenTitles,
    onSaved,
    onClose,
}: AddCategoryFormProps) {
    const { t } = useTranslation();
    const { toast } = useToast();
    const [title, setTitle] = useState('');
    const [saving, setSaving] = useState(false);
    const canAdd = !saving && !!title.trim();

    const add = async () => {
        const next = title.trim();
        if (!canAdd) return;
        if (
            takenTitles.some(
                (taken) => taken.toLowerCase() === next.toLowerCase()
            )
        ) {
            toast({
                title: t('departmentVariables.category.titleExists'),
                variant: 'destructive',
            });
            return;
        }
        setSaving(true);
        try {
            await createCategory({ label: next, tab });
            await onSaved();
            onClose();
        } catch {
            return;
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card className="border-subtle p-4 flex items-center gap-2">
            <Input
                autoFocus
                value={title}
                placeholder={t(
                    'departmentVariables.category.categoryPlaceholder'
                )}
                onChange={(e) => setTitle(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.preventDefault();
                        void add();
                    }
                    if (e.key === 'Escape') onClose();
                }}
            />
            <Button variant="outline" size="sm" onClick={onClose}>
                {t('departmentVariables.category.cancel')}
            </Button>
            <Button size="sm" onClick={() => void add()} disabled={!canAdd}>
                {t('departmentVariables.category.add')}
            </Button>
        </Card>
    );
}
